"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { Box } from "@chakra-ui/react";
import { Package } from "iconoir-react";
import { useState } from "react";
const untouched = (imageUrl) => ({
    askedAbout: imageUrl,
    state: "stored",
    refreshedUrl: null,
});
/**
 * The product as a square thumbnail. Without an image it keeps its box and
 * shows a muted glyph rather than collapsing: the column stays aligned whether
 * the shop holds a picture or not, which is the whole point of the marker.
 *
 * A gift stores the CDN URL the product photo had at send time, so a merchant
 * replacing or deleting that photo leaves a URL nothing will ever serve again.
 * The placeholder is therefore the SECOND answer, not the first: a dead image
 * asks the shop what it has now, and only an empty answer gives up. One attempt
 * per stored URL, remembered across re-renders — a row redrawing itself is not
 * new information, and a thumb that asked on every paint would be a loop.
 *
 * The image is decorative — the product is named right beside it — so its alt
 * is empty and it never doubles the line a screen reader already reads.
 */
export const GiftProductThumb = ({ imageUrl, size = 28, onImageBroken, }) => {
    const [progress, setProgress] = useState(() => untouched(imageUrl));
    // A different product, or the same one re-imaged elsewhere, is a different
    // question: the verdict is dropped so the new URL gets its own chance. Read
    // during render rather than reset in an effect, so no paint ever shows the
    // previous product's verdict on this one.
    const current = progress.askedAbout === imageUrl ? progress : untouched(imageUrl);
    const state = imageUrl === null ? "placeholder" : current.state;
    const shown = state === "refreshed"
        ? current.refreshedUrl
        : state === "placeholder"
            ? null
            : imageUrl;
    const giveUp = () => setProgress({
        askedAbout: imageUrl,
        state: "placeholder",
        refreshedUrl: null,
    });
    const handleError = () => {
        // The shop has been asked and has not answered yet. A second failure of
        // the same dead URL is not news, and acting on it would throw away the
        // answer that is already on its way.
        if (state === "asking")
            return;
        // Anything but the stored URL failing is the end of the road: either
        // nobody can ask, or the shop has just told us what it serves and there is
        // nothing further to ask.
        if (state !== "stored" || !onImageBroken) {
            giveUp();
            return;
        }
        const askedAbout = imageUrl;
        setProgress({ askedAbout, state: "asking", refreshedUrl: null });
        // Only the question still open is answered: a product swapped underneath
        // us, or a verdict already reached, must not be overwritten by a reply to
        // a question nobody is waiting on.
        const settle = (next) => setProgress((live) => live.askedAbout === askedAbout && live.state === "asking" ? next : live);
        onImageBroken().then((fresh) => settle(fresh
            ? { askedAbout, state: "refreshed", refreshedUrl: fresh }
            : { askedAbout, state: "placeholder", refreshedUrl: null }), () => settle({ askedAbout, state: "placeholder", refreshedUrl: null }));
    };
    return (_jsx(Box, { flex: "none", display: "grid", placeItems: "center", boxSize: `${size}px`, borderWidth: "1px", borderColor: "border", borderRadius: "6px", overflow: "hidden", bg: "bg.subtle", color: "fg.subtle", _dark: { bg: "gray.900", color: "gray.400", borderColor: "gray.700" }, "data-testid": "gift-product-thumb", "data-image-state": state, children: shown ? (_jsx("img", { src: shown, alt: "", loading: "lazy", onError: handleError, style: { width: "100%", height: "100%", objectFit: "cover" } }, shown)) : (_jsx(Package, { width: Math.round(size / 2), height: Math.round(size / 2) })) }));
};
